import React, { useEffect, useState, useCallback } from "react";
import { Search, FileText, Download, Share2, Trash2, Loader2, Archive, RefreshCw, FolderOpen, FolderInput, Star } from "lucide-react";
import { helperListSigned, helperSignedFileUrl, helperDeleteSigned, helperSignedFolders, helperMoveSigned } from "../lib/api";
import { toast } from "sonner";

export default function SignedDocs({ userId, refreshKey, onChanged }) {
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("");
  const [importantOnly, setImportantOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const loadFolders = useCallback(async () => {
    try { const r = await helperSignedFolders(userId); setFolders(r.folders || []); } catch (e) {}
  }, [userId]);

  const load = useCallback(async (query = "", folder = "", important = false) => {
    setLoading(true);
    try { const r = await helperListSigned(userId, query, folder, important); setDocs(r.documents || []); }
    catch (e) { toast.error("Could not load your signed documents"); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { loadFolders(); }, [loadFolders, refreshKey]);
  useEffect(() => { const t = setTimeout(() => load(q, activeFolder, importantOnly), 300); return () => clearTimeout(t); }, [q, activeFolder, importantOnly, load, refreshKey]);

  const openFile = (d) => window.open(helperSignedFileUrl(d.id, userId), "_blank");

  const share = async (d) => {
    setBusyId(d.id);
    try {
      const res = await fetch(helperSignedFileUrl(d.id, userId));
      const blob = await res.blob();
      const file = new File([blob], `${(d.title || "document").replace(/[^a-z0-9]+/gi, "_").slice(0, 50)}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: d.title });
      } else {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = file.name; a.click(); URL.revokeObjectURL(a.href);
        toast.message("Downloaded — attach it in WhatsApp");
      }
    } catch (e) { /* cancelled or failed */ }
    finally { setBusyId(null); }
  };

  const move = async (d) => {
    const folder = window.prompt("Move to folder (type a name, or leave blank for none):", d.folder || "");
    if (folder === null) return;
    try {
      await helperMoveSigned(d.id, userId, folder.trim());
      toast.success(folder.trim() ? `Moved to “${folder.trim()}”` : "Removed from folder");
      await loadFolders(); await load(q, activeFolder, importantOnly); onChanged?.();
    } catch (e) { toast.error("Could not move"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? This removes the saved PDF.`)) return;
    try { await helperDeleteSigned(d.id, userId); setDocs((p) => p.filter((x) => x.id !== d.id)); toast.success("Deleted"); await loadFolders(); onChanged?.(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch (e) { return ""; } };
  const chip = (active) => `px-3 py-1 rounded-full nx-mono text-[11px] border transition-colors ${active ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/70 border-cyan-200/25 hover:border-cyan-200/50"}`;

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 space-y-3" data-testid="signed-docs-panel">
      <div className="flex items-center gap-2">
        <Archive className="w-4 h-4 text-[#00F0FF]" />
        <p className="text-cyan-200/70 text-xs nx-mono tracking-widest">SIGNED DOCUMENTS — {docs.length} shown</p>
        <button onClick={() => { loadFolders(); load(q, activeFolder, importantOnly); }} data-testid="signed-refresh" className="ml-auto text-cyan-200/50 hover:text-[#00F0FF]"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-cyan-200/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="signed-search"
          placeholder="Search by title, signer, place, tag or folder…"
          className="w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]" />
      </div>

      <div className="flex flex-wrap gap-1.5 items-center" data-testid="signed-folder-chips">
        <button onClick={() => setImportantOnly((v) => !v)} data-testid="signed-important-filter" className={`px-3 py-1 rounded-full nx-mono text-[11px] border transition-colors flex items-center gap-1 ${importantOnly ? "text-[#030712] bg-[#F5C542] border-[#F5C542]" : "text-[#F5C542] border-[#F5C542]/40 hover:border-[#F5C542]"}`}><Star className={`w-3 h-3 ${importantOnly ? "fill-[#030712]" : ""}`} /> Important</button>
        {folders.length > 0 && <button onClick={() => setActiveFolder("")} data-testid="signed-folder-all" className={chip(!activeFolder)}>All folders</button>}
        {folders.map((f) => (
          <button key={f.name} onClick={() => setActiveFolder(f.name === activeFolder ? "" : f.name)} data-testid="signed-folder-chip" className={chip(activeFolder === f.name)}>
            <FolderOpen className="w-3 h-3 inline mr-1 -mt-0.5" />{f.name} ({f.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
      ) : docs.length === 0 ? (
        <div className="py-16 flex flex-col items-center justify-center text-center text-cyan-100/30 gap-2">
          <Archive className="w-8 h-8" />
          <p className="text-sm nx-mono">{q || activeFolder || importantOnly ? "No matches — try another search or folder" : "No signed documents yet. Sign a PDF and it lands here."}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="signed-list">
          {docs.map((d) => (
            <div key={d.id} data-testid="signed-item" className="flex items-center gap-3 p-3 rounded-xl bg-[#0a1526] border border-[#00F0FF22] hover:border-[#00F0FF55] transition-colors">
              <div className="w-9 h-9 rounded-lg bg-[#00F0FF11] flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-[#00F0FF]" /></div>
              <button onClick={() => openFile(d)} data-testid="signed-open" className="flex-1 min-w-0 text-left">
                <p className="text-cyan-50 text-sm font-semibold truncate flex items-center gap-1.5">{d.important ? <Star className="w-3.5 h-3.5 text-[#F5C542] fill-[#F5C542] shrink-0" /> : null}{d.title}{d.folder ? <span className="ml-1 text-[10px] text-[#00F0FF] bg-[#00F0FF15] px-1.5 py-0.5 rounded-full nx-mono">{d.folder}</span> : null}</p>
                <p className="text-cyan-100/40 text-[11px] nx-mono truncate">{[d.signer, fmtDate(d.created_at), d.place].filter(Boolean).join(" · ")}{d.keywords ? ` · #${d.keywords.split(/[,\s]+/).filter(Boolean).join(" #")}` : ""}</p>
              </button>
              <button onClick={() => share(d)} disabled={busyId === d.id} data-testid="signed-share" title="Send via WhatsApp / Share" className="p-2 rounded-lg text-[#25D366] hover:bg-[#25D36615]">{busyId === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}</button>
              <button onClick={() => openFile(d)} data-testid="signed-download" title="Open / Download" className="p-2 rounded-lg text-[#00F0FF] hover:bg-[#00F0FF15]"><Download className="w-4 h-4" /></button>
              <button onClick={() => move(d)} data-testid="signed-move" title="Move to folder" className="p-2 rounded-lg text-cyan-100/60 hover:text-[#00F0FF] hover:bg-[#00F0FF15]"><FolderInput className="w-4 h-4" /></button>
              <button onClick={() => remove(d)} data-testid="signed-delete" title="Delete" className="p-2 rounded-lg text-cyan-100/40 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
